import React, { useEffect, useRef, useState } from 'react';
import { useSiteContent } from '../hooks/useSiteContent';

function formatCounter(val, suffix) {
  if (val >= 10000) return `${Math.floor(val / 1000)}K${suffix}`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K${suffix}`;
  return `${val}${suffix}`;
}

function Counter({ to, suffix }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const duration = 1800;
          const step = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setVal(Math.floor(eased * to));
            if (t < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.4 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [to]);

  return <span ref={ref} className="tabular">{formatCounter(val, suffix)}</span>;
}

export default function Stats() {
  const { content } = useSiteContent();
  const stats = content.stats || [];
  return (
    <section className="relative py-12 sm:py-20 lg:py-24 bg-zinc-950 overflow-hidden">
      <div className="absolute inset-0 diagonal-stripes opacity-50" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 stagger">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`group relative text-center ${
                i === 0 ? 'sm:text-left sm:justify-self-start' :
                i === stats.length - 1 ? 'sm:text-right sm:justify-self-end' :
                'sm:text-center sm:justify-self-center'
              }`}
            >
              <div className={`absolute -top-2 text-[10px] tracking-[0.3em] text-red-500 font-bold ${
                i === stats.length - 1 ? 'sm:-right-2 sm:left-auto -left-2' : '-left-2'
              }`}>0{i + 1}</div>
              <div className="font-display text-6xl md:text-7xl text-white group-hover:text-red-500 transition-colors duration-500">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 text-xs tracking-[0.3em] text-zinc-500">{s.label}</div>
              <div className={`mt-4 h-[2px] w-12 bg-red-600 group-hover:w-full transition-all duration-700 ${
                i === stats.length - 1 ? 'sm:ml-auto' : i === 1 ? 'sm:mx-auto' : ''
              }`} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
